import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Camera, FileText, MessageSquare, Activity, ArrowRight, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function Welcome() {
  const [showInstructions, setShowInstructions] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleContinue = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40" data-testid="welcome-page">
      <div className="container mx-auto px-4 py-12">
        {/* Welcome Header */}
        <div className="text-center mb-12 animate-fadeIn">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-[#1e3a8a] via-[#2c57c5] to-slate-700 bg-clip-text text-transparent mb-4" data-testid="welcome-title">
            Bienvenido al Portal de Pago de SIEMPRE
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Hola {user?.name}, gestiona tus pagos de manera fácil y segura
          </p>
        </div>

        {/* Instructions Card */}
        {showInstructions && (
          <Card className="max-w-4xl mx-auto p-8 mb-8 relative glass animate-slideUp" data-testid="instructions-card">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4"
              onClick={() => setShowInstructions(false)}
              data-testid="skip-instructions-button"
            >
              <X className="h-5 w-5" />
            </Button>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">¿Cómo adjuntar documentos?</h2>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-indigo-100 rounded-full p-3 flex-shrink-0">
                  <span className="text-[#1e3a8a] font-bold text-lg">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Completa el formulario de registro</h3>
                  <p className="text-slate-600">Ingresa la información del proveedor y los datos de la factura</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-indigo-100 rounded-full p-3 flex-shrink-0">
                  <span className="text-[#1e3a8a] font-bold text-lg">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Adjunta tus documentos</h3>
                  <p className="text-slate-600">Puedes subir PDFs o fotografías. También usar la cámara para escanear documentos</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-indigo-100 rounded-full p-3 flex-shrink-0">
                  <span className="text-[#1e3a8a] font-bold text-lg">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Revisa la bitácora y chatea</h3>
                  <p className="text-slate-600">Todas las acciones quedan registradas. Puedes comunicarte mediante el chat</p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <Button
                onClick={() => setShowInstructions(false)}
                className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white"
                data-testid="close-instructions-button"
              >
                Entendido
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Card>
        )}

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <Card className="p-6 card-hover cursor-pointer" data-testid="feature-camera">
            <div className="bg-blue-100 rounded-full p-4 w-fit mb-4">
              <Camera className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Escáner de Cámara</h3>
            <p className="text-sm text-slate-600">Captura fotos o escanea documentos directamente</p>
          </Card>

          <Card className="p-6 card-hover cursor-pointer" data-testid="feature-files">
            <div className="bg-green-100 rounded-full p-4 w-fit mb-4">
              <FileText className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Adjuntar Archivos</h3>
            <p className="text-sm text-slate-600">Sube PDFs y fotografías de forma segura</p>
          </Card>

          <Card className="p-6 card-hover cursor-pointer" data-testid="feature-chat">
            <div className="bg-purple-100 rounded-full p-4 w-fit mb-4">
              <MessageSquare className="h-8 w-8 text-purple-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Chat Integrado</h3>
            <p className="text-sm text-slate-600">Comunícate con otros usuarios en tiempo real</p>
          </Card>

          <Card className="p-6 card-hover cursor-pointer" data-testid="feature-log">
            <div className="bg-orange-100 rounded-full p-4 w-fit mb-4">
              <Activity className="h-8 w-8 text-orange-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Bitácora de Acciones</h3>
            <p className="text-sm text-slate-600">Rastrea todas las actividades realizadas</p>
          </Card>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <Button
            onClick={handleContinue}
            size="lg"
            className="bg-gradient-to-r from-[#1e3a8a] to-[#2c57c5] hover:from-[#1e3a8a] hover:to-[#1e40af] text-white px-8 py-6 text-lg shadow-lg"
            data-testid="continue-to-dashboard-button"
          >
            Ir al Panel de Pagos
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
